import { useQuery } from '@tanstack/react-query';
import { normalizeSubscriptionTier, type SubscriptionTier } from '@court-sync/shared';
import { academyService } from '../services/academy';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys } from './keys';

export interface OwnerStripeTierData {
  slug: string;
  ownerStripeTier: SubscriptionTier | null;
  hasPaymentProcessing: boolean;
}

async function loadOwnerStripeTierData(slug: string): Promise<OwnerStripeTierData> {
  const data = await academyService.getAcademySubscriptionPlan(slug);
  return {
    slug,
    ownerStripeTier: normalizeSubscriptionTier(data.tier) ?? null,
    hasPaymentProcessing: data.hasPaymentProcessing,
  };
}

export async function fetchOwnerStripeTierQuery(
  slug: string,
  options?: { force?: boolean }
): Promise<OwnerStripeTierData> {
  const key = queryKeys.academies.ownerStripeTier(slug);
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: () => loadOwnerStripeTierData(slug),
    staleTime: STALE_TIME.LONG,
  });
}

export function useOwnerStripeTier(slug?: string | null, options?: { enabled?: boolean }) {
  const { data, isLoading, isError, error, isSuccess, isFetching } = useQuery({
    queryKey: queryKeys.academies.ownerStripeTier(slug ?? ''),
    queryFn: () => loadOwnerStripeTierData(slug!),
    enabled: !!slug && (options?.enabled ?? true),
    staleTime: STALE_TIME.LONG,
  });

  return {
    ownerStripeTier: data?.ownerStripeTier ?? null,
    // Default to false before data loads so payment-gated UI never flashes for
    // academies without processing; gate on `status === 'success'` for the real value.
    hasPaymentProcessing: data?.hasPaymentProcessing ?? false,
    isLoading,
    isError,
    isSuccess,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    status: isLoading || isFetching
      ? ('loading' as const)
      : isError
        ? ('error' as const)
        : isSuccess
          ? ('success' as const)
          : undefined,
  };
}
